/**
 * Environment for the guest web app.
 *
 * Everything here is public by design: Firebase web config and the Agora App ID
 * ship to the browser in every deployment and are not secrets. What must never
 * appear in this app — or anywhere in `apps/` — is the Agora App Certificate.
 * Tokens are minted by `getAgoraCredentials` in Cloud Functions.
 */

import { DEFAULT_FUNCTIONS_REGION } from '@hotel-voice/shared';

function required(name: string, value: string | undefined): string {
  if (!value || value.trim().length === 0) {
    throw new Error(
      `Missing ${name}. Copy apps/guest-web/.env.example to .env.local and fill it in.`,
    );
  }
  return value.trim();
}

const env = import.meta.env;

export const firebaseConfig = {
  apiKey: required('VITE_FIREBASE_API_KEY', env.VITE_FIREBASE_API_KEY),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN', env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: required('VITE_FIREBASE_PROJECT_ID', env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  messagingSenderId: required(
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  ),
  appId: required('VITE_FIREBASE_APP_ID', env.VITE_FIREBASE_APP_ID),
};

/**
 * Shared default, so this can never drift from the region the functions are
 * actually deployed to. Override only alongside a matching backend override.
 */
export const functionsRegion =
  env.VITE_FUNCTIONS_REGION?.trim() || DEFAULT_FUNCTIONS_REGION;

/**
 * No Agora config here on purpose. The App ID arrives per call inside the
 * `createCall` response, alongside the channel, UID and short-lived token —
 * so there is exactly one source of Agora identity (Cloud Functions) and no
 * second copy in a client that could drift out of sync with it.
 */

/** reCAPTCHA Enterprise site key. Empty disables App Check (local dev). */
export const appCheckSiteKey = env.VITE_APPCHECK_SITE_KEY?.trim() ?? '';

export const useEmulators = env.VITE_USE_EMULATORS === 'true';

export const emulatorHost = env.VITE_EMULATOR_HOST?.trim() || '127.0.0.1';
